// REST layer. Mirrors the Flutter app exactly: plain JSON POSTs to the same
// backend (no auth header — the server's encryptedAuth middleware is pass-through,
// just like the Flutter client relies on).

import axios from 'axios';
import { localFirst, localFirstOne } from './localFirst';
import * as local from '../db/localRepo';

export const API_BASE =
  process.env.REACT_APP_API_URL || 'http://smartifyindia.co.in:8080/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// `config` is passed straight to axios, so a caller can override the default
// timeout for an endpoint known to be slow.
const post = (endpoint, body = {}, config) => api.post(`/${endpoint}`, body, config);

// ---------------- Auth / user ----------------
export const login = (mobileNumber, password) =>
  post('GetUserDetailsbyMobileNumberAndPassword', {
    MobileNumber: mobileNumber,
    Password: password,
  });

// ServiceBy must match the Flutter client exactly — the backend uses it as the
// sender name in the WhatsApp template.
export const sendOtp = (mobileNumber) =>
  post('SendOtpOnWhatsAppbyMobileNumber', {
    MobileNumber: mobileNumber,
    ServiceBy: 'Smart Life India',
  });

export const registerUser = (payload) => post('AddNewUser', payload);

export const updateUser = (data, where) =>
  post('UpdateUsers', { data, where });

// ---------------- Companies (Super Admin) ----------------
export const getCompanies = () => post('GetDataFromCompanyInfo', {});
export const addCompany = (payload) => post('AddCompanyInfo', payload);
export const updateCompany = (data, where) =>
  post('UpdateCompanyInfo', { data, where });

// ---------------- Device Types (Super Admin catalog) ----------------
// The product/device-type catalog (homeautomation.DeviceType). The list query
// joins ImageData + OTABinFile, so a row carries more columns than the table —
// send ONLY DeviceType columns back on add/update (see DEVICE_TYPE_COLUMNS).
export const getDeviceTypes = () => post('GetAllDeviceTypeDetails', {});

// AddDeviceType upserts on DeviceName (body IS the row).
export const addDeviceType = (row) => post('AddDeviceType', row);

// UpdateDeviceType takes { data, where } like every other Update endpoint.
export const updateDeviceType = (data, where) =>
  post('UpdateDeviceType', { data, where });

// ---------------- Locations ----------------
// LOCAL-FIRST from here down (structure only). On a native build these read the
// SQLite cache and only touch the network when it can't answer — that is what
// keeps a normal launch free of structure calls. Live status and control are
// deliberately NOT cached; they still go over MQTT/REST every time.
export const getLocations = (userId) => localFirst(
  () => local.getLocations(userId),
  () => post('GetLocationsByUserId', { UserId: userId }),
);

export const getLocationDetail = (locationId) =>
  post('GetLocationDetailsbyLocationID', { LocationID: locationId });

// ---------------- Rooms ----------------
export const getRooms = (userId, locationId) => localFirst(
  () => local.getRooms(userId, locationId),
  () => post('GetRoomsbyUserIDandLocationId', { UserId: userId, LocationID: locationId }),
);

export const getCommonDevices = (userId, locationId) => localFirst(
  () => local.getCommonDevices(locationId),
  () => post('GetCommonDevicesbyUserIDandLocationId', {
    UserId: userId,
    LocationID: locationId,
  }),
);

export const getDevicesByRoom = (userId, roomId) => localFirst(
  () => local.getRoomDevices(roomId),
  () => post('GetDeviceDetailsbyUserIdAndRoomId', { UserId: userId, RoomID: roomId }),
);

// ---------------- Reorder (SortOrder) ----------------
// Locations + devices are per-user (mapping tables); rooms are global (Rooms table).
export const updateLocationSortOrder = (userId, locationId, sortOrder) =>
  post('UpdateUserLocationMappingbyUserIdAndLocationID', {
    data: { SortOrder: sortOrder },
    where: `UserId = ${userId} AND LocationID = ${locationId}`,
  });

export const updateRoomSortOrder = (roomId, sortOrder) =>
  post('UpdateRooms', {
    data: { SortOrder: sortOrder },
    where: `RoomID = ${roomId}`,
  });

export const updateDeviceSortOrder = (userId, deviceInstalledId, sortOrder) =>
  post('UpdateUserDeviceMappingByUserIDAndDeviceInstalledID', {
    data: { SortOrder: sortOrder },
    where: `UserId = ${userId} AND DeviceInstalledID = ${deviceInstalledId}`,
  });

// ---------------- Devices ----------------
export const getDevicesByUser = (userId) => localFirst(
  () => local.getAllDevices(),
  () => post('GetListofDevicesbyUserId', { UserId: userId }),
);

// Every device at a location, REGARDLESS OF WHO OWNS IT — no UserId in the
// request. Deliberately not local-first: the cache only holds the signed-in
// user's devices, so serving from it would silently return a subset.
//
// For the Super Admin console, which works on any unique number on the broker.
// Returns a thin row: UniqueNumber, RoomName, DeviceName, DeviceTypeName,
// NetworkLastUpdatedDateTime, Connected.
export const getDevicesByLocation = (locationId) =>
  post('GetAllDevicesByLocationID', { LocationID: locationId });

export const getFavouriteDevices = (userId) => localFirst(
  () => local.getFavouriteDevices(),
  () => post('GetListoffavouriteDevicesbyUserId', { UserId: userId }),
);

export const getDeviceDetail = (uniqueNumber) => localFirstOne(
  () => local.getDeviceDetail(uniqueNumber),
  () => post('GetDeviceDetailbyUniqueNumber', { UniqueNumber: uniqueNumber }),
);

export const getDeviceStatus = (uniqueNumber) =>
  post('GetDeviceStatusbyUniqueNumber', { UniqueNumber: uniqueNumber });

export const getOutputs = (uniqueNumber) => localFirst(
  () => local.getOutputs(uniqueNumber),
  () => post('GetOutputDetailbyUniqueNumber', { UniqueNumber: uniqueNumber }),
);

// Current-sense readings for a phase meter on a given day (YYYY-M-D).
// Returns rows { DateTime, current, power, phase } (~every 30-60s).
export const getCurrentSense = (uniqueNumber, dateStr) =>
  post('GetCurrentSenseByUniqueNumberAndDateOnly', {
    UniqueNumber: uniqueNumber,
    DateTime: dateStr,
  });

export const getButtonsByLocationAndUnique = (locationId, uniqueNumber) =>
  post('GetButtonsbyLocationIdAndUniqueNumber', {
    LocationID: locationId,
    UniqueNumber: uniqueNumber,
  });

export const updateOutputName = (outputName, uniqueNumber, output) =>
  post('UpdateOutputDetails', {
    data: { OutputName: outputName },
    where: `UniqueNumber = '${uniqueNumber}' AND Output = '${output}'`,
  });

// ---------------- Device settings (rename, output props) ----------------
export const updateDeviceName = (uniqueNumber, deviceName) =>
  post('UpdateDeviceInstalledByUniqueNumber', {
    data: { DeviceName: deviceName },
    UniqueNumber: uniqueNumber,
  });

// Generic per-output update ({ OutputName } now; button props later).
export const updateOutputDetail = (uniqueNumber, output, data) =>
  post('UpdateOutputDetailsByUniqueNumberAndOutput', {
    data,
    UniqueNumber: uniqueNumber,
    Output: output,
  });

export const setFavourite = (deviceInstalledId, userId, favourite) =>
  post('UpdateUserDeviceMapping', {
    data: { favourite: favourite ? 1 : 0 },
    where: `DeviceInstalledID = ${deviceInstalledId} AND UserId = ${userId}`,
  });

// ---------------- Schedules ----------------
export const getSchedules = (uniqueNumber, output) =>
  post('GetSchedulerListByUniqueNumberAndOutput', {
    UniqueNumber: uniqueNumber,
    Output: output,
  });

export const addSchedule = (payload) => post('AddSchedulerList', payload);

// ---------------- Two-way ("router") mappings ----------------
// Every mapping involving this device, EITHER side — a button can be the first
// or the second half of a pair, so callers must check both.
//
// ⚠️ The column is `FristButtonNumber` — the backend's typo, not ours. Spelling
// it correctly returns nothing.
export const getTwoWayMappings = (uniqueNumber) =>
  post('GetTwoWayAllButtonsDetailsByUniqueNumber', { UniqueNumber: uniqueNumber });

// ---------------- Device sharing / claiming ----------------
// Map an existing installed device to a user. Used both when accepting a shared
// device and when adding one manually by Unique Number.
// Note the string coercion — the Dart sends both as strings.
export const addUserDeviceMapping = (userId, deviceInstalledId) =>
  post('AddUserDeviceMapping', {
    UserId: String(userId),
    DeviceInstalledID: String(deviceInstalledId),
  });
export const updateSchedule = (data, where) =>
  post('UpdateSchedulerList', { data, where });
export const deleteSchedule = (where) =>
  post('DeleteSchedulerList', { where });

// ---------------- Wall Mode ----------------
export const getWallPanels = (userId) =>
  post('GetWallPanelsByUserId', { UserId: userId });
export const addWallPanel = (payload) => post('AddWallPanel', payload);
export const updateWallPanel = (data, where) =>
  post('UpdateWallPanel', { data, where });
export const deleteWallPanel = (where) => post('DeleteWallPanel', { where });

export const addWallButton = (payload) => post('AddWallButton', payload);
export const updateWallButton = (data, where) =>
  post('UpdateWallButton', { data, where });
export const deleteWallButton = (where) => post('DeleteWallButton', { where });

export const addWallButtonAction = (payload) =>
  post('AddWallButtonAction', payload);
export const deleteWallButtonActionsByButtonId = (wallButtonId) =>
  post('DeleteWallButtonActionsByWallButtonId', { WallButtonId: wallButtonId });

// ═══════════════════════════════════════════════════════════════════════════
// Endpoints ported from the Flutter app (SmartLifeIndia-flutter). Bodies match
// the Dart callers byte-for-byte — see docs/08-flutter-parity-plan.md.
// ═══════════════════════════════════════════════════════════════════════════

// ---------------- Profile image ----------------
// linker.dart getImageId() — resolves Users.ImageId / DevicesInstalled.ImageId
// to a URL. Returns rows [{ ImageUrl }].
export const getImageById = (imageId) =>
  post('GetImageDatabyImageId', { ImageId: imageId });

// ---------------- Locations (create / edit) ----------------
// locationFunctions.dart addLocation() → returns { insertId }.
export const addLocation = (payload) => post('AddLocation', payload);

export const addUserLocationMapping = (userId, locationId) =>
  post('AddUserLocationMapping', { UserId: userId, LocationID: locationId });

export const updateLocation = (locationId, data) =>
  post('UpdateLocation', { data, where: `LocationID='${locationId}'` });

// ---------------- Location WiFi ----------------
// The authoritative list of networks for a site — one row per SSID, with the
// password, shared by every device installed there.
//
// ⚠️ BROKEN ON THE SERVER as of 2026-07-28. The route exists (it does not 404)
// but NEVER SENDS A RESPONSE — verified against LocationID 29, which is real and
// has devices, and against ids that match nothing. Flutter calls the same
// endpoint (locationFunctions.dart getWiFis) and hangs identically, so this is
// not a porting difference. Every caller must therefore treat a failure as
// "unavailable", never as "this location has no networks".
//
// The explicit timeout is what turns an indefinite hang into a failure a caller
// can handle; without it the awaiting dialog spins forever.
export const getLocationWifi = (locationId) =>
  post('GetAllWifiNetworksByLocationId', { LocationID: locationId }, { timeout: 10000 });

export const addLocationWifi = (locationId, ssid, password) =>
  post('AddWifiNameAndWifiPasswordByLocationId', {
    LocationID: locationId,
    SSID: ssid,
    Password: password,
  });

// ---------------- Rooms (create) ----------------
// roomScreenFunctions.dart addRoom() → returns { insertId }.
export const addRoom = (roomName, locationId) =>
  post('AddRooms', { RoomName: roomName, LocationID: Number(locationId) });

export const addLocationRoomsMapping = (userId, locationId, roomId) =>
  post('AddLocationRoomsMapping', {
    UserId: userId,
    LocationID: Number(locationId),
    RoomID: Number(roomId),
  });

// ---------------- Devices (generic install-row update) ----------------
// The Flutter app writes DevicesInstalled through UpdateDeviceInstalled with a
// { data, where } pair — used for rename, BodyColor and BodyType. Kept separate
// from updateDeviceName() above (which uses the ByUniqueNumber variant) so both
// wire formats stay available.
export const updateDeviceInstalled = (uniqueNumber, data) =>
  post('UpdateDeviceInstalled', {
    data,
    where: `UniqueNumber = '${uniqueNumber}'`,
  });

export const renameDeviceInstalled = (uniqueNumber, deviceName) =>
  updateDeviceInstalled(uniqueNumber, { DeviceName: deviceName });

// BodyColor: 0 Black · 1 White. BodyType: 0 Cona · 1 GM · 2 Goldmedal.
export const setDeviceBodyColor = (uniqueNumber, color) =>
  updateDeviceInstalled(uniqueNumber, { BodyColor: color });

export const setDeviceBodyType = (uniqueNumber, type) =>
  updateDeviceInstalled(uniqueNumber, { BodyType: type });

// ---------------- Remove a device ----------------
// homeScreenFunctions.dart removeDevice(): unassign the install row, then drop
// this user's mapping to it.
export const unassignDevice = (uniqueNumber) =>
  updateDeviceInstalled(uniqueNumber, { MasterUserId: 0 });

export const deleteUserDeviceMapping = (deviceInstalledId, userId) =>
  post('DeleteFromUserDeviceMapping', {
    where: `DeviceInstalledID = ${deviceInstalledId} and UserId = ${userId}`,
  });

// ---------------- User registration ----------------
// linker.dart getRegistered() — field names and the ISD:91 literal are the
// Flutter payload exactly.
export const registerNewUser = ({
  name, middleName, lastName, password, email, mobile, deviceName,
}) =>
  post('AddNewUser', {
    Surname: lastName,
    Name: name,
    FatherHusband: middleName,
    Password: password,
    ISD: 91,
    MobileNumber: Number(mobile),
    EmailId: email,
    DeviceName: deviceName,
  });

// Bind this phone to the account (single-device login enforcement).
export const updateUserDeviceName = (userId, deviceName) =>
  post('UpdateUsers', { data: { DeviceName: deviceName }, where: `UserId=${userId}` });

// forgotPassFunctions.dart updateId(): a password reset ALSO re-binds the
// account to the phone doing the reset, which is what logs it out elsewhere.
export const resetPasswordAndBindDevice = (mobileNumber, password, deviceName) =>
  post('UpdateUsers', {
    data: { DeviceName: deviceName, Password: password },
    where: `MobileNumber=${mobileNumber}`,
  });

// Clears the server's "local cache is stale" flag after a successful sync.
export const clearSinkData = (userId) =>
  post('UpdateUsers', { data: { SinkData: '0' }, where: `UserId = ${userId}` });

export default api;
